import { Node } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import PlanningImageNodeView from '@/components/editor/PlanningImageNodeView.vue'

export const IMAGE_SIZES = ['s', 'm', 'l']

/**
 * Figure-style image node for the detailplaneering rich editor (RED-268).
 *
 * The node's inline content IS the caption ("Detailplaneeringu põhijoonis…" /
 * "Allikas: …") — real document content rendered under the image, so the
 * toolbar marks (bold/italic/underline) apply to it and the export renders it
 * as paragraphs below the image, split on hard breaks (Enter inside the
 * caption inserts a line break instead of splitting the figure).
 *
 * Deliberately not @tiptap/extension-image: the attrs are an exact whitelist
 * mirrored by the PHP export converter (photoId resolves an owned photos-table
 * row; size maps to a fixed export width), and parseHTML requires
 * data-photo-id so foreign <img> tags pasted from the web are dropped instead
 * of smuggling arbitrary srcs into the document.
 */
export const PlanningImage = Node.create({
  name: 'planningImage',

  group: 'block',

  content: 'inline*',

  draggable: true,

  selectable: true,

  addAttributes() {
    return {
      photoId: { default: null },
      src: { default: null },
      size: {
        default: 'm',
        parseHTML: element => {
          const size = element.getAttribute('data-size')
          return IMAGE_SIZES.includes(size) ? size : 'm'
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'figure[data-planning-image]',
        getAttrs: element => ({
          photoId: Number(element.getAttribute('data-photo-id')) || null,
          src: element.getAttribute('data-src'),
        }),
        contentElement: 'figcaption',
      },
      {
        tag: 'img[data-photo-id]',
        getAttrs: element => ({
          photoId: Number(element.getAttribute('data-photo-id')) || null,
          src: element.getAttribute('src'),
        }),
      },
    ]
  },

  renderHTML({ node }) {
    return [
      'figure',
      {
        'data-planning-image': '',
        'data-photo-id': node.attrs.photoId,
        'data-src': node.attrs.src,
        'data-size': node.attrs.size,
      },
      ['img', { src: node.attrs.src, 'data-photo-id': node.attrs.photoId }],
      ['figcaption', 0],
    ]
  },

  addKeyboardShortcuts() {
    return {
      // Inside the caption, Enter adds a caption line instead of splitting
      // the figure into a captionless clone.
      Enter: () => {
        const { $from } = this.editor.state.selection
        if ($from.parent.type.name !== this.name) {
          return false
        }
        return this.editor.commands.setHardBreak()
      },
    }
  },

  addNodeView() {
    return VueNodeViewRenderer(PlanningImageNodeView)
  },
})
